import React from "react"
import { Outlet, type RouteObject } from "react-router-dom"

import { PermissionGate } from "../../../../components/PermissionGate/PermissionGate.tsx"
import { RoutesPaths } from "../../../../constants/routes.ts"
import { COUNTERSIGN_PERMISSION } from "../../../Esign/hooks/use-countersign.ts"
import { SEND_FOR_SIGNATURE_PERMISSION } from "../../../Esign/hooks/use-send-for-signature.ts"

import { lazyPage } from "../lazyPage.tsx"
import { useAppSelector } from "../../../../hooks/use-app-selector.ts"
import { canSeeSignatureRequests } from "../../../Esign/helpers/signature-requests-access.ts"
import { SignatureRequestsGate } from "../../../Esign/TrackingPage/SignatureRequestsGate.tsx"
import { OpenInWorkspace } from "../../../Esign/workspace/OpenInWorkspace.tsx"

const SignatureRequestsPage = lazyPage(
  () => import("../../../Esign/TrackingPage/SignatureRequestsPage.tsx")
)
// A modal over the manager: nothing in the page below while its file loads.
const EsignComposePage = lazyPage(
  () => import("../../../Esign/EsignComposePage.tsx"),
  { loading: null }
)

const gated = (
  <PermissionGate paths={[SEND_FOR_SIGNATURE_PERMISSION]}>
    <EsignComposePage />
  </PermissionGate>
)

// The wizard is a modal over EZ-Sign, as New Lead is over the leads list: one
// layout keeps the manager mounted while the wizard and the workspace open over it.
const managerWithWizard = (
  <>
    <SignatureRequestsGate>
      <SignatureRequestsPage />
    </SignatureRequestsGate>
    <Outlet />
  </>
)

// Signing for the company does not need the list's own access: the manager is drawn
// behind the workspace only for someone who may see it.
function SignBehindManager() {
  const pages = useAppSelector((state) => state.auth.permissions?.pages)
  return (
    <>
      {canSeeSignatureRequests(pages) && <SignatureRequestsPage />}
      <OpenInWorkspace view="sign" />
    </>
  )
}

// Without the right to countersign nothing of the request is read, behind or in front.
const gatedSigning = (
  <PermissionGate paths={[COUNTERSIGN_PERMISSION]}>
    <SignBehindManager />
  </PermissionGate>
)

// Ahead of envelopePath, or "/sign" would be read as an envelope id. A request's
// own addresses open it in the EZ-Sign workspace, over the manager.
export const esignRoutes: RouteObject[] = [
  {
    element: managerWithWizard,
    children: [
      { path: RoutesPaths.signatureRequests, element: null },
      { path: RoutesPaths.esign.newEnvelopePath, element: gated },
      { path: RoutesPaths.esign.envelopePath, element: gated },
      {
        path: RoutesPaths.esign.requestPath,
        element: <OpenInWorkspace view="overview" />
      }
    ]
  },
  { path: RoutesPaths.esign.signPath, element: gatedSigning }
]
